import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
	EditQuizPage,
	EditQuizRef,
	convertProctoringAssessmentConfiguration,
	convertProctoringOption,
} from "./editQuiz";
import { api as defaultApi, VerityApi } from "../api/fetch";
import { createPortal } from "react-dom";
import EnableVeritySwitch from "./enableVeritySwitch";
import ThemeWrapper from "../theme";
import styles from "./editQuizWrapper.module.css";
import { EditQuizSwitchComponent, getDefaultProctoringOption } from "./editQuizWrapper";
import {
	ApiClientProctoringAssessmentConfigurationV2,
	ApiClientProctoringOptionV2,
	AssessmentInfo,
} from "@verity/api-client/types";
import { CoreButton, CoreModal, CoreToastContainer, coreToast } from "../core";
import password from "./password";
import {
	ProctoringAssessmentConfigurationV2,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION } from "@verity/proctoring-option";
import { ExemptedStudent } from "../types/common";
import { VerityLogger } from "@yujacorp/verity-logger";
import { ConfigurationPageName } from "./proctoring-options/option-text";
import { ShadowDomContext } from "./shadow-dom-context";

export type UnsavedAssessmentInfo = Partial<AssessmentInfo>;

export type EditQuizWrapperProps = {
	api?: VerityApi;
	switchComponent?: EditQuizSwitchComponent;
	quizInfo: UnsavedAssessmentInfo;
	saveTemporaryProctoringAssessmentConfiguration: (options: {
		assessmentInfo: UnsavedAssessmentInfo;
		courseInfo?: {
			name?: string;
			code?: string;
		};
		profileId?: number;
		proctoringAssessmentConfiguration?:
			| Partial<Omit<ApiClientProctoringAssessmentConfigurationV2, "proctoringOptionProfile">>
			| undefined;
		proctoringOption: Partial<ApiClientProctoringOptionV2> | undefined;
		exemptedStudents: Omit<ExemptedStudent, "name">[];
	}) => Promise<void>;
	portalElement?: HTMLElement;
	cancelButtonHandler?: () => void;
	onSave?: () => void;
	onInitialOptions?: (
		proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
		proctoringOption: Partial<ProctoringOption>,
	) => void;
	onEnabledChange: (
		proctoringAssessmentConfiguration: Pick<Partial<ProctoringAssessmentConfigurationV2>, "enabled" | "password">,
		onPasswordGenerated: () => void,
	) => void | Promise<void>;
	onModalOpenChange?: (open: boolean) => void;
	forceThirdParty?: boolean;
	passwordFillTextOffset?: number;
	name?: string;
	url?: string;
	logger?: VerityLogger;
	editButtonStyles?: React.CSSProperties;
	// Need reference to shadow container for Mui Select. Once we switch to iframe we can remove this prop
	shadowContainer: HTMLElement;

	/* Dictionary of references to external HTML elements to use for events (Non-React) */
	externalRefs?: Record<string, HTMLElement>;
	initialPlatformId?: number;
};

export type EditQuizModalWrapperRef = {
	proctoringAssessmentConfiguration?: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">;
	proctoringOption?: Partial<ProctoringOption>;
	outerSaveButtonHandler: (params: {
		additionalQuizInfo?: Partial<AssessmentInfo>;
		urlList?: string[];
	}) => Promise<void>;
	setEnabled: (enabled: boolean) => void;
	getEnabled: () => boolean;
	triggerOpenModal: (pageName: ConfigurationPageName) => void;
};

export const EditQuizModalWrapper = forwardRef(function EditQuizModalWrapper(
	{
		api = defaultApi,
		switchComponent,
		quizInfo,
		saveTemporaryProctoringAssessmentConfiguration,
		portalElement,
		cancelButtonHandler,
		onInitialOptions,
		onEnabledChange,
		onModalOpenChange,
		forceThirdParty = false,
		name,
		url,
		editButtonStyles,
		logger,
		externalRefs,
		shadowContainer,
		initialPlatformId,
		onSave,
	}: EditQuizWrapperProps,
	ref: ForwardedRef<EditQuizModalWrapperRef>,
) {
	const {
		getProctoringAssessmentConfiguration,
		saveProctoringAssessmentConfiguration,
		areProctoringProfilesEnforced,
		getDefaultProctoringProfile,
		getVerityInstituteSettings,
		getUserInstituteId,
		validateAssessment,
	} = api;

	const [initialProctoringAssessmentConfiguration, setInitialProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [proctoringAssessmentConfiguration, setProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [initialProctoringOption, setInitialProctoringOption] = useState<Partial<ProctoringOption>>();
	const [proctoringOption, setProctoringOption] = useState<Partial<ProctoringOption>>();
	const [currentProfileId, setCurrentProfileId] = useState<number>();
	const [enforcedProfilesOnly, setEnforcedProfilesOnly] = useState<boolean>(true);
	const [initialEnabledValue, setInitialEnabledValue] = useState<boolean | undefined>(undefined);

	const [modalShowing, setModalShowing] = useState(false);
	const updateModalStatus = (showing: boolean) => {
		populateThirdPartyURL(externalRefs);
		setModalShowing(showing);
		onModalOpenChange?.(showing);
		if (showing) {
			void logger?.assessment.edit.configurationModal.opened();
		} else {
			void logger?.assessment.edit.configurationModal.closed();
		}
	};
	const closeModal = () => updateModalStatus(false);
	const [backEnabled, setBackEnabled] = useState(true);
	const [nextEnabled, setNextEnabled] = useState(true);
	const [isFinalPage, setIsFinalPage] = useState(false);
	const [saveEnabled, setSaveEnabled] = useState(false);
	const [closeEnabled, setCloseEnabled] = useState(true);

	const editQuizPageRef = useRef<EditQuizRef>(null);

	//the exemptedstudentInfo that is used to upload
	const [uploadedExemptedStudentsMap, setUploadedExemptedStudentsMap] = useState<Map<number, ExemptedStudent>>(
		new Map(),
	);

	const [uploadedProfilesMap, setUploadedProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	const [defaultProctoringOption, setDefaultProctoringOption] = useState<ProctoringOption | null>(null);

	const outerSaveButtonHandler: EditQuizModalWrapperRef["outerSaveButtonHandler"] = async ({
		additionalQuizInfo,
		urlList,
	}) => {
		void logger?.assessment.edit.saveButton.saveStarted({
			additionalQuizInfo,
			urlList,
		});

		if (!quizInfo.quizId) {
			try {
				await saveTemporaryProctoringAssessmentConfiguration({
					assessmentInfo: quizInfo,
					proctoringAssessmentConfiguration: proctoringAssessmentConfiguration,
					proctoringOption: proctoringOption,
					exemptedStudents: Array.from(uploadedExemptedStudentsMap.values()).filter(
						(x) => !x.proctoringOptionProfileId || uploadedProfilesMap.has(x.proctoringOptionProfileId),
					),
				});
				void logger?.assessment.edit.saveButton.saveFinished(true);
			} catch (e) {
				void logger?.assessment.edit.saveButton.saveFinished(false);
				throw e;
			}
			return;
		}

		const { quizId } = quizInfo;

		let newProctoringAssessmentConfiguration: Partial<ProctoringAssessmentConfigurationV2> | undefined;
		const newProctoringOption: Partial<ProctoringOption> = {};
		if (proctoringAssessmentConfiguration) {
			newProctoringAssessmentConfiguration = { enabled: proctoringAssessmentConfiguration.enabled };
			if (proctoringAssessmentConfiguration.password) {
				newProctoringAssessmentConfiguration.password = proctoringAssessmentConfiguration.password;
			}
			if (urlList) {
				newProctoringOption.tabWhiteList = urlList;
			}
			const thirdPartyUrlInputRef = externalRefs?.["thirdPartyUrlInputRef"]
				? (externalRefs.thirdPartyUrlInputRef as HTMLInputElement)
				: undefined;
			const finalThirdPartyUrl = thirdPartyUrlInputRef?.value || thirdPartyUrl;
			if (finalThirdPartyUrl && proctoringAssessmentConfiguration.thirdPartyAssessmentOption) {
				newProctoringAssessmentConfiguration.thirdPartyAssessmentOption = {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: finalThirdPartyUrl,
				};
			}
		}
		try {
			await saveProctoringAssessmentConfiguration({
				assessmentInfo: {
					...quizInfo,
					...(additionalQuizInfo ?? {}),
					quizId,
				},
				proctoringAssessmentConfiguration: newProctoringAssessmentConfiguration,
				proctoringOption:
					Object.keys(newProctoringOption).length > 0 ? { ...proctoringOption, ...newProctoringOption } : undefined,
			});
			void logger?.assessment.edit.saveButton.saveFinished(true);
		} catch (e) {
			void logger?.assessment.edit.saveButton.saveFinished(false);
			throw e;
		}
	};

	const setEnabled: EditQuizModalWrapperRef["setEnabled"] = async (enabled) => {
		if (!proctoringAssessmentConfiguration || !quizInfo.courseId) return;
		proctoringAssessmentConfiguration.enabled = enabled;
		const instituteId = await getUserInstituteId();
		proctoringAssessmentConfiguration.password = proctoringAssessmentConfiguration.password
			? proctoringAssessmentConfiguration.password
			: quizInfo.quizId
				? password.generateKey(instituteId, quizInfo.courseId, quizInfo.quizId)
				: null;
		try {
			if (proctoringAssessmentConfiguration) {
				setProctoringAssessmentConfiguration({ ...proctoringAssessmentConfiguration });
			}
			await onEnabledChange(proctoringAssessmentConfiguration, () => undefined);
		} catch (err) {
			console.error(err);
			alert("Something went wrong.");
		}
		if (!proctoringAssessmentConfiguration.enabled) {
			setSaveEnabled(true);
		}
	};

	/* Retrieves the current status of the switch */
	const getEnabled: EditQuizModalWrapperRef["getEnabled"] = () => {
		return Boolean(proctoringAssessmentConfiguration && proctoringAssessmentConfiguration?.enabled);
	};

	useImperativeHandle(ref, () => ({
		proctoringAssessmentConfiguration,
		proctoringOption,
		outerSaveButtonHandler,
		setEnabled,
		getEnabled,
		triggerOpenModal,
	}));

	const refreshSettings = (confirmedOption?: Partial<ProctoringOption>) => {
		void (async () => {
			setEnforcedProfilesOnly(await areProctoringProfilesEnforced());
			const defaultProfile = await getDefaultProctoringProfile();
			const verityInstituteSettings = await getVerityInstituteSettings();
			const defaultProctoringOption = getDefaultProctoringOption(verityInstituteSettings);
			setDefaultProctoringOption(defaultProctoringOption);
			const defaultInitialProctoringOption = defaultProfile?.proctoringOption ?? defaultProctoringOption;

			const quizId = quizInfo?.quizId;
			let getProctoringAssessmentConfigurationResp: ProctoringAssessmentConfigurationV2 | null = null;
			if (quizId) {
				const quizInfoWithId = {
					...quizInfo,
					quizId,
				};
				getProctoringAssessmentConfigurationResp = await getProctoringAssessmentConfiguration(quizInfoWithId);
			}

			if (getProctoringAssessmentConfigurationResp && quizId) {
				const { proctoringOptionProfile, ...configuration } = getProctoringAssessmentConfigurationResp;
				const convertedConfiguration = convertProctoringAssessmentConfiguration(
					configuration,
					forceThirdParty,
					initialPlatformId,
				);
				if (initialEnabledValue === undefined) {
					setInitialEnabledValue(convertedConfiguration.enabled);
				}
				setInitialProctoringAssessmentConfiguration({ ...convertedConfiguration });
				setProctoringAssessmentConfiguration(convertedConfiguration);
				setInitialProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
				setProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
				setCurrentProfileId(proctoringOptionProfile.id);
				onInitialOptions?.(configuration, proctoringOptionProfile.proctoringOption);

				/**
				 * If we have initial options, and Verity is enabled, validate the configuration
				 */
				if (convertedConfiguration.enabled) {
					try {
						const { didValidate, ...validations } = await validateAssessment({
							courseId: quizInfo.courseId,
							quizId,
						});

						if (!didValidate) return;

						if (validations.isPasswordRequired && !validations.doPasswordsMatch) {
							coreToast.error(
								"The proctoring settings could not be validated. Please resave the settings for the assessment.",
							);
						}
					} catch (e) {
						void logger?.error(e);
					}
				}
			} else {
				const convertedConfiguration = convertProctoringAssessmentConfiguration(
					{ ...DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION, enabled: getEnabled() },
					forceThirdParty,
					initialPlatformId,
				);
				if (initialEnabledValue === undefined || !quizInfo.quizId) {
					setInitialEnabledValue(convertedConfiguration.enabled);
				}
				if (convertedConfiguration.enabled && proctoringAssessmentConfiguration?.password) {
					convertedConfiguration.password = proctoringAssessmentConfiguration.password;
				}
				setInitialProctoringAssessmentConfiguration(proctoringAssessmentConfiguration ?? { ...convertedConfiguration });
				setProctoringAssessmentConfiguration(proctoringAssessmentConfiguration ?? convertedConfiguration);
				setInitialProctoringOption(confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
				setProctoringOption(confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
				setCurrentProfileId(defaultProfile?.id);
				onInitialOptions?.({}, confirmedOption ?? initialProctoringOption ?? defaultInitialProctoringOption);
			}
		})();
	};

	useEffect(() => {
		refreshSettings();
	}, []);

	/* Trigger Opening of the modal to a specified page */
	const triggerOpenModal: EditQuizModalWrapperRef["triggerOpenModal"] = (pageName: ConfigurationPageName) => {
		editQuizPageRef.current?.goToPage(pageName);
		setModalShowing(true);
	};
	const [handledExternalRefs, setHandledExternalRefs] = useState<boolean>(false);
	const [thirdPartyUrl, setThirdPartyUrl] = useState<string>();
	/* Use Effect to handle external references */
	useEffect(() => {
		/* Do nothing if already handled */
		if (handledExternalRefs) {
			return;
		}

		/* Do nothing if there is no proctoring assessment configuration or reference */
		if (!proctoringAssessmentConfiguration || !externalRefs) {
			return;
		}

		/* If we have the reference to the external input field, update the state */
		if (proctoringAssessmentConfiguration.thirdPartyAssessmentOption && externalRefs["thirdPartyUrlInputRef"]) {
			const thirdPartyUrlInputRef = externalRefs["thirdPartyUrlInputRef"] as HTMLInputElement;
			let finalValidURL = "";
			if (!thirdPartyUrlInputRef.value) {
				const hiddenInput = document.getElementById(
					"assignment_external_tool_tag_attributes_custom_params",
				) as HTMLInputElement;
				const encodedValue = hiddenInput?.value;

				if (encodedValue) {
					try {
						const decoded = encodedValue.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
						const parsed = JSON.parse(decoded);
						finalValidURL = parsed.redirectURL;
					} catch (err) {
						console.error("Error parsing custom_params:", err);
					}
				}
			} else {
				finalValidURL = thirdPartyUrlInputRef.value;
			}

			/* Update the state immediately */
			setProctoringAssessmentConfiguration({
				...proctoringAssessmentConfiguration,
				thirdPartyAssessmentOption: {
					...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
					thirdPartyUrl: finalValidURL,
				},
			});
			setThirdPartyUrl(finalValidURL);

			/* Handler for input update */
			const updateValue = () => {
				setProctoringAssessmentConfiguration({
					...proctoringAssessmentConfiguration,
					thirdPartyAssessmentOption: {
						...proctoringAssessmentConfiguration.thirdPartyAssessmentOption,
						thirdPartyUrl: thirdPartyUrlInputRef?.value,
					},
				});
				setThirdPartyUrl(thirdPartyUrlInputRef.value);
			};

			/* Append listener and return */
			thirdPartyUrlInputRef.addEventListener("input", updateValue);
			setHandledExternalRefs(true);
		}
	}, [proctoringAssessmentConfiguration]);

	let optionsPage;
	let editButton;
	if (proctoringAssessmentConfiguration?.enabled) {
		optionsPage = (
			<ThemeWrapper>
				<CoreModal
					open={modalShowing}
					onClose={() => {
						editQuizPageRef.current?.closeButtonHandler();
						cancelButtonHandler?.();
					}}
					title={"Setup Proctoring"}
					minWidth="min(80%, 960px)"
					minHeight="min(90%, 660px)"
					widthFixed
					heightFixed
					hideHorizontalOverflow
					hideVerticalOverflow
					padding="12px 36px 0px 36px"
					disableTopCloseButton={!closeEnabled}
					buttons={[
						{
							text: "Cancel",
							color: "secondary",
							disabled: !closeEnabled,
							onClick: () => {
								editQuizPageRef.current?.closeButtonHandler();
								cancelButtonHandler?.();
							},
							sx: { marginRight: "auto" },
						},
						{
							text: "Back",
							color: "primary",
							onClick: () => editQuizPageRef.current?.backButtonHandler(),
							disabled: !backEnabled,
						},
						{
							text: "Save as Profile",
							color: "primary",
							disabled: !closeEnabled,
							hide: enforcedProfilesOnly || !isFinalPage,
							onClick: () => {
								editQuizPageRef.current?.saveAsProfile();
							},
						},
						isFinalPage
							? {
									text: "Confirm",
									color: "primary",
									disabled: !saveEnabled || !closeEnabled,
									onClick: () => {
										editQuizPageRef.current?.saveButtonHandler().catch(console.error);
										onSave?.();
									},
								}
							: {
									text: "Next",
									color: "primary",
									disabled: !nextEnabled,
									onClick: () => editQuizPageRef.current?.nextButtonHandler(),
								},
					]}
				>
					<EditQuizPage
						quizName={name}
						url={thirdPartyUrl ?? url}
						ref={editQuizPageRef}
						api={api}
						quizInfo={quizInfo}
						initialProctoringAssessmentConfiguration={initialProctoringAssessmentConfiguration}
						proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
						setProctoringAssessmentConfiguration={setProctoringAssessmentConfiguration}
						initialProctoringOption={initialProctoringOption}
						proctoringOption={proctoringOption}
						setProctoringOption={setProctoringOption}
						currentProfileId={currentProfileId}
						setCurrentProfileId={setCurrentProfileId}
						enforcedProfilesOnly={enforcedProfilesOnly}
						onBackEnabledChange={setBackEnabled}
						onNextEnabledChange={setNextEnabled}
						onSaveEnabledChange={setSaveEnabled}
						onClose={() => {
							closeModal();
							refreshSettings();
						}}
						setIsFinalPage={setIsFinalPage}
						onStartSaveOptions={(confirmedOptions?: Partial<ProctoringOption>) => {
							setCloseEnabled(false);
							coreToast.loading("Applying your proctor settings...");
							refreshSettings(confirmedOptions);
						}}
						onSaveOptions={(success, messages) => {
							setCloseEnabled(true);
							if (success) {
								if (proctoringAssessmentConfiguration?.enabled) {
									setInitialEnabledValue(true);
								}
								coreToast.dismiss();
								coreToast.success("Your proctor settings have been successfully applied to the assessment.");
								cancelButtonHandler?.();
								closeModal();
							} else {
								coreToast.dismiss();
								coreToast.error(
									<>
										Failed to apply your proctor settings to the assessment{messages ? ": " : "."}
										{messages?.map((message) => (
											<div key={message}>{message}</div>
										))}
									</>,
								);
							}
						}}
						uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
						setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
						uploadedProfilesMap={uploadedProfilesMap}
						setUploadedProfilesMap={setUploadedProfilesMap}
						logger={logger}
						externalRefs={externalRefs}
						defaultProctoringOption={defaultProctoringOption}
						forceStartOnFirstPage={!initialEnabledValue}
					/>
				</CoreModal>
				<CoreToastContainer enableMultiContainer />
			</ThemeWrapper>
		);

		const defaultEditButtonStyles: React.CSSProperties = {
			backgroundColor: "#fff",
		};
		const editButtonWrapperStyles = editButtonStyles
			? { ...defaultEditButtonStyles, ...editButtonStyles }
			: defaultEditButtonStyles;

		editButton = (
			<ThemeWrapper>
				<div style={editButtonWrapperStyles}>
					<CoreButton onClick={() => updateModalStatus(true)}>Configure Settings</CoreButton>
				</div>
			</ThemeWrapper>
		);

		if (portalElement) {
			editButton = createPortal(editButton, portalElement);
			optionsPage = createPortal(optionsPage, portalElement);
		} else {
			editButton = <div className={styles.root}>{editButton}</div>;
			optionsPage = <div className={styles.root}>{optionsPage}</div>;
		}
	}

	const Switch = switchComponent ?? EnableVeritySwitch;

	function populateThirdPartyURL(externalRefs: Record<string, HTMLElement> | undefined) {
		try {
			const thirdPartyUrlInputRef = externalRefs?.["thirdPartyUrlInputRef"] as HTMLInputElement;
			let finalValidURL = "";
			if (!thirdPartyUrlInputRef.value) {
				const hiddenInput = document.getElementById(
					"assignment_external_tool_tag_attributes_custom_params",
				) as HTMLInputElement;
				const encodedValue = hiddenInput?.value;

				if (encodedValue) {
					const decoded = encodedValue.replace(/&quot;/g, '"').replace(/&amp;/g, "&");
					const parsed = JSON.parse(decoded);
					finalValidURL = parsed.redirectURL;
				}
			} else {
				finalValidURL = thirdPartyUrlInputRef.value;
			}

			/* Update the state immediately */
			setProctoringAssessmentConfiguration({
				...proctoringAssessmentConfiguration,
				thirdPartyAssessmentOption: {
					...proctoringAssessmentConfiguration?.thirdPartyAssessmentOption,
					thirdPartyUrl: finalValidURL,
				},
			});
			setThirdPartyUrl(finalValidURL);
		} catch {
			return;
		}
	}

	return (
		<ShadowDomContext.Provider value={shadowContainer}>
			<div id="root">
				{proctoringAssessmentConfiguration && proctoringOption && (
					<ThemeWrapper>
						<div style={{ position: "relative" }}>
							<Switch enabled={Boolean(proctoringAssessmentConfiguration.enabled)} setEnabled={setEnabled} />
						</div>
					</ThemeWrapper>
				)}
				{editButton}
				{optionsPage}
			</div>
		</ShadowDomContext.Provider>
	);
});
